// Makes stored KB HTML interactive after render: tabs, auto table of contents,
// LaTeX equations and internal article links that survive slug changes.
import katex from "katex";
import "katex/dist/katex.min.css";
import api from "@/lib/api";
import { decodeHtml, htmlBlockFrame } from "@/lib/kbHtmlSandbox";

function wireTabs(root) {
  root.querySelectorAll("div.kb-tabs").forEach((box) => {
    if (box.dataset.kbWired) return;
    box.dataset.kbWired = "1";
    const panels = [...box.querySelectorAll(":scope > .kb-tab-panel")];
    if (!panels.length) return;
    const bar = document.createElement("div");
    bar.className = "kb-tabbar";
    panels.forEach((panel, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "kb-tab-btn";
      btn.textContent = panel.getAttribute("data-kb-title") || `Tab ${i + 1}`;
      btn.setAttribute("data-testid", `kb-tab-btn-${i}`);
      btn.addEventListener("click", () => {
        panels.forEach((p, j) => p.classList.toggle("kb-tab-active", i === j));
        [...bar.children].forEach((b, j) => b.classList.toggle("kb-tab-btn-active", i === j));
      });
      bar.appendChild(btn);
      panel.classList.toggle("kb-tab-active", i === 0);
    });
    bar.firstChild?.classList.add("kb-tab-btn-active");
    box.prepend(bar);
  });
}

function fillToc(root) {
  const blocks = root.querySelectorAll("div.kb-toc");
  if (!blocks.length) return;
  const heads = [...root.querySelectorAll("h2, h3")].filter((h) => h.textContent.trim());
  heads.forEach((h, i) => { if (!h.id) h.id = `kb-h-${i}`; });
  blocks.forEach((box) => {
    box.innerHTML = "";
    const title = document.createElement("div");
    title.className = "kb-toc-title";
    title.textContent = "Contents";
    box.appendChild(title);
    const list = document.createElement("ol");
    heads.forEach((h) => {
      const li = document.createElement("li");
      li.className = h.tagName === "H3" ? "kb-toc-sub" : "";
      const a = document.createElement("a");
      a.href = `#${h.id}`;
      a.textContent = h.textContent.trim();
      a.addEventListener("click", (e) => {
        e.preventDefault();
        h.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      li.appendChild(a);
      list.appendChild(li);
    });
    if (!heads.length) {
      const p = document.createElement("p");
      p.className = "kb-toc-fallback";
      p.textContent = "Add headings to this article to build the contents list.";
      box.appendChild(p);
      return;
    }
    box.appendChild(list);
  });
}

let mermaidReady = null;

async function renderDiagrams(root) {
  const blocks = [...root.querySelectorAll("div.kb-mermaid")].filter((el) => !el.dataset.kbRendered);
  if (!blocks.length) return;
  if (!mermaidReady) {
    mermaidReady = import("mermaid").then(({ default: mermaid }) => {
      mermaid.initialize({ startOnLoad: false, theme: "neutral", securityLevel: "strict" });
      return mermaid;
    });
  }
  const mermaid = await mermaidReady;
  await Promise.all(blocks.map(async (el, i) => {
    // The <pre> keeps real newlines; the attribute is only a fallback because
    // HTML attributes collapse whitespace.
    const pre = el.querySelector("pre.kb-mermaid-src");
    const code = ((pre ? pre.textContent : el.getAttribute("data-kb-code")) || "").trim();
    el.dataset.kbRendered = "1";
    try {
      const { svg } = await mermaid.render(`kb-mmd-${Date.now()}-${i}`, code);
      const caption = el.getAttribute("data-kb-title");
      el.innerHTML = svg + (caption ? `<figcaption>${caption}</figcaption>` : "");
    } catch (e) {
      el.innerHTML = `<pre class="kb-mermaid-error">${code}</pre>`;
      console.warn("KB diagram failed to render", e);
    }
  }));
}

async function hydrateSnippets(root) {
  const blocks = [...root.querySelectorAll("div.kb-snippet[data-kb-snippet]")];
  const ids = [...new Set(blocks.map((el) => el.getAttribute("data-kb-snippet")).filter(Boolean))];
  if (!ids.length) return;
  const { data } = await api.post("/kb/snippets/resolve", { snippet_ids: ids });
  blocks.forEach((el) => {
    const hit = data.snippets?.[el.getAttribute("data-kb-snippet")];
    const label = el.getAttribute("data-kb-title") || "Reusable block";
    if (hit && !hit.missing && hit.body_html) {
      el.innerHTML = `<div class="kb-snippet-body">${hit.body_html}</div>`
        + `<div class="kb-snippet-source">Reusable block: ${hit.name}</div>`;
    } else {
      el.classList.add("kb-snippet-missing");
      el.innerHTML = `<div class="kb-snippet-source">Reusable block “${label}” is no longer available.</div>`;
    }
  });
}

function renderEquations(root) {
  root.querySelectorAll("div.kb-equation").forEach((el) => {
    if (el.dataset.kbRendered) return;
    const latex = el.getAttribute("data-kb-latex") || el.textContent || "";
    try {
      katex.render(latex, el, { displayMode: true, throwOnError: false });
      el.dataset.kbRendered = "1";
    } catch {
      el.textContent = latex;
    }
  });
}

const GLOSSARY_SKIP = "a,code,pre,h1,h2,h3,h4,th,button,.kb-glossary-term,.kb-mermaid,.kb-equation,.kb-toc";
let glossaryPromise = null;

/** Reset between page loads so a newly added term shows up without a refresh. */
export function resetGlossaryCache() {
  glossaryPromise = null;
}

function glossaryEntries(terms) {
  const entries = [];
  terms.forEach((t) => {
    [t.term, ...(t.aliases || [])].forEach((phrase) => {
      if (phrase && phrase.trim()) entries.push({ phrase: phrase.trim(), term: t });
    });
  });
  // Longest phrase first so "SIP trunk" wins over "SIP".
  return entries.sort((a, b) => b.phrase.length - a.phrase.length);
}

function glossaryChip(matched, term) {
  const span = document.createElement("span");
  span.className = "kb-glossary-term";
  span.tabIndex = 0;
  span.setAttribute("data-testid", `kb-glossary-term-${term.term_id}`);
  span.appendChild(document.createTextNode(matched));

  const card = document.createElement("span");
  card.className = "kb-glossary-card";
  card.setAttribute("data-testid", `kb-glossary-card-${term.term_id}`);
  const head = document.createElement("span");
  head.className = "kb-glossary-card-term";
  head.textContent = term.term;                     // textContent — never HTML
  const def = document.createElement("span");
  def.className = "kb-glossary-card-def";
  def.textContent = term.definition;
  card.appendChild(head);
  card.appendChild(def);
  if (term.link_slug) {
    const a = document.createElement("a");
    a.className = "kb-glossary-card-link";
    a.href = `/kb/a/${term.link_slug}`;
    a.textContent = "Read the full article →";
    card.appendChild(a);
  }
  span.appendChild(card);
  return span;
}

/** Underline the FIRST occurrence of each glossary term and attach a hover
 *  card with its definition. One pass, never inside links/code/headings. */
async function linkGlossary(root) {
  // Set the guard BEFORE awaiting — the reader triggers enhancement more than
  // once per render and concurrent passes would each mark another occurrence.
  if (root.dataset.kbGlossary) return;
  root.dataset.kbGlossary = "1";
  if (!glossaryPromise) {
    glossaryPromise = api.get("/kb/glossary")
      .then((r) => r.data?.terms || [])
      .catch(() => []);
  }
  const terms = await glossaryPromise;
  if (!terms.length) return;
  const entries = glossaryEntries(terms);
  const done = new Set();

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      if (node.parentElement?.closest(GLOSSARY_SKIP)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  nodes.forEach((node) => {
    for (const { phrase, term } of entries) {
      if (done.has(term.term_id)) continue;
      const re = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
      const hit = re.exec(node.nodeValue);
      if (!hit) continue;
      const after = node.splitText(hit.index);
      after.nodeValue = after.nodeValue.slice(hit[0].length);
      node.parentNode.insertBefore(glossaryChip(hit[0], term), after);
      done.add(term.term_id);
      return;                       // one term per text node keeps offsets sane
    }
  });
}

async function resolveInternalLinks(root) {
  const links = [...root.querySelectorAll("a[data-kb-article]")];
  const ids = [...new Set(links.map((a) => a.getAttribute("data-kb-article")).filter(Boolean))];
  if (!ids.length) return;
  try {
    const { data } = await api.post("/kb/resolve-links", { article_ids: ids });
    links.forEach((a) => {
      const hit = data.links?.[a.getAttribute("data-kb-article")];
      if (hit?.slug) {
        a.setAttribute("href", `/kb/a/${hit.slug}`);
        a.classList.add("kb-internal-link");
        if (hit.missing) a.classList.add("kb-internal-link-missing");
      } else {
        a.classList.add("kb-internal-link", "kb-internal-link-missing");
        a.title = "This article no longer exists";
      }
    });
  } catch (e) {
    console.warn("KB internal links could not be resolved", e);
  }
}

/** Every code block gets a copy button — techs were selecting 200 lines of SBC
 *  config by hand. The language, when we know it, doubles as a label. */
const COLLAPSE_AFTER = 24;

/** Clipboard with the old-school fallback, for browsers that refuse the API
 *  outside a secure context. */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

/** Every config block on the page, in reading order, each under the heading it
 *  sits below — a tech setting up an SBC wants the lot in one paste. */
export function collectCodeBlocks(root) {
  if (!root) return [];
  return [...root.querySelectorAll("pre")].map((pre) => {
    let heading = "";
    let el = pre.closest(".kb-code-wrap") || pre;
    while (el && !heading) {
      el = el.previousElementSibling || el.parentElement;
      if (!el || el.classList?.contains("kb-prose")) break;
      if (/^H[1-6]$/.test(el.tagName || "")) heading = el.textContent.trim();
    }
    return { heading, code: (pre.querySelector("code") || pre).innerText };
  });
}

function addCopyButtons(root) {
  root.querySelectorAll("pre").forEach((pre) => {
    if (pre.dataset.kbCopy) return;
    pre.dataset.kbCopy = "1";
    const code = pre.querySelector("code");
    const wrap = document.createElement("div");
    wrap.className = "kb-code-wrap";
    pre.parentNode.insertBefore(wrap, pre);
    wrap.appendChild(pre);

    const lang = [...(code?.classList || [])]
      .find((c) => c.startsWith("language-"))?.slice(9) || "";
    const lines = ((code || pre).textContent.match(/\n/g) || []).length + 1;
    // A one-line command doesn't need a label sitting on top of it, and
    // "PLAIN" tells nobody anything.
    if (lang && !["plaintext", "plain", "text", "none", "undefined"].includes(lang) && lines > 2) {
      const tag = document.createElement("span");
      tag.className = "kb-code-lang";
      tag.textContent = lang;
      wrap.appendChild(tag);
    }

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "kb-code-copy";
    btn.textContent = "Copy";
    btn.setAttribute("data-testid", "kb-code-copy");
    btn.setAttribute("aria-label", "Copy this code");
    btn.addEventListener("click", async () => {
      await copyText((code || pre).innerText);
      btn.textContent = "Copied";
      btn.classList.add("kb-code-copied");
      setTimeout(() => {
        btn.textContent = "Copy";
        btn.classList.remove("kb-code-copied");
      }, 1600);
    });
    wrap.appendChild(btn);

    // A 100-line SBC config shouldn't push the rest of the article off the
    // screen, and scrolling the page through it loses your place. Long blocks
    // get their own scroll box with an honest line count.
    if (lines > COLLAPSE_AFTER) {
      wrap.classList.add("kb-code-tall");
      const more = document.createElement("button");
      more.type = "button";
      more.className = "kb-code-expand";
      more.setAttribute("data-testid", "kb-code-expand");
      const label = () => {
        more.textContent = wrap.classList.contains("kb-code-open")
          ? "Collapse" : `Show all ${lines} lines`;
      };
      more.addEventListener("click", () => {
        wrap.classList.toggle("kb-code-open");
        label();
        if (!wrap.classList.contains("kb-code-open")) {
          pre.scrollTop = 0;
          wrap.scrollIntoView({ block: "nearest" });
        }
      });
      label();
      wrap.appendChild(more);
    }
  });
}

/** Swap each stored interactive block for its sandboxed iframe. */
function mountHtmlBlocks(root) {
  root.querySelectorAll("div.kb-html-block, div[data-kb-block='html']").forEach((el) => {
    if (el.dataset.kbMounted) return;
    const html = decodeHtml(el.getAttribute("data-kb-html") || "");
    if (!html) return;
    el.dataset.kbMounted = "1";
    el.innerHTML = "";
    el.appendChild(htmlBlockFrame(html, {
      height: Number(el.getAttribute("data-kb-height")) || 320,
      title: el.getAttribute("data-kb-title") || "",
      dark: document.documentElement.classList.contains("dark"),
    }));
  });
}

/** Run every enhancement over a rendered article body. One failing block
 *  must never take the rest of the article down with it. */
export function enhanceKbContent(root, { resolveLinks = true, glossary = false } = {}) {
  if (!root) return;
  const steps = [["tabs", wireTabs], ["toc", fillToc], ["equations", renderEquations],
                 ["html blocks", mountHtmlBlocks], ["copy buttons", addCopyButtons]];
  steps.forEach(([name, fn]) => {
    try { fn(root); } catch (e) { console.warn(`KB content: ${name} failed`, e); }
  });
  renderDiagrams(root).catch((e) => console.warn("KB diagrams failed", e));
  if (resolveLinks) {
    hydrateSnippets(root)
      .then(() => { try { renderDiagrams(root); } catch { /* nested diagram */ } })
      .then(() => (glossary ? linkGlossary(root) : null))
      .catch((e) => console.warn("KB snippets failed", e));
    resolveInternalLinks(root).catch((e) => console.warn("KB links failed", e));
  } else if (glossary) {
    linkGlossary(root).catch((e) => console.warn("KB glossary failed", e));
  }
}
